import request, { MethodEnum, serverTriggerUrl } from '@/utils/fetch';
import type { IArticalAuther, IThreadLisItem } from '@/api/thread/list';
import { handleResourceUrl, ThreadReplySortEnum } from '@/api/thread';
import { thread_reg } from '@/utils/reg';
import { ReactHTML } from 'react';
import type { IEmoji } from '@/api/common';

export { IArticalAuther };

export enum DETAIL_APIS {
  BASE_URL = '/forum/thread/',
  THREAD_DETAIL = '/forum/thread/info/',
  LIKE_THREAD = '/like',
  COLLECT_THREAD = '/favorite',
  BANNER_AD = '/forum/banner/ad',
  SHARE_THREAD = '/share',
  OPERATE_THREAD_BY_ADMIN = '/forum/thread/admin',
  REPLY_THREAD_OPERATION = '/forum/thread/reply/operation',
  REPLY = '/reply',
}

// 置顶枚举
export enum DisplayOrderEnum {
  GLOBAL = 3, // 全局置顶
  CATEGORY = 2, // 分类置顶
  SELF = 1, // 本板块置顶
  NONE = 0, // 不置顶
  PRIVATE = -2, // 非公开
}

// Admin 操作
export enum AdminOperationEnum {
  DELETE = 'delete',
  STICK = 'stick', // 置顶
  UNSTICK = 'un_stick', // 取消置顶
  DIGEST = 'digest', // 加精
  UNDIGEST = 'un_digest', // 取消加精
}

export enum ChannelTagDisplayTypeEnum {
  ACTIVITY = 'ACTIVITY', // 首页-活动
  GROUP = 'GROUP', // 设备圈
  THEME = 'THEME', // 设备主题页
}

export interface IError {
  code: number;
  message: string;
}

export interface IError {
  code: number;
  message: string;
}

export interface IAuthorThread {
  tid: number;
  dateline: number;
  subject: string; // 标题
}

export type IAuhtor = Omit<IArticalAuther, 'device' | 'rank'> & {
  // 分数
  credits: number;
  // 签名
  signature: string;
  is_friend: boolean;
  // 最新主题
  threads: IAuthorThread[];
  devices: Array<NonNullable<IArticalAuther['device']>>;
  rank: NonNullable<IArticalAuther['rank']>;
};

export interface IForumType {
  fid: number;
  forum_name: string;
  type_id: number;
  name: string;
  level: number;
  parent?: {
    fid: number;
    forum_name: string;
    type_id: number;
    name: string;
    level: number;
  };
}

export interface IChannelTagType {
  id: number;
  slug: string;
  name: string;
  display_type: string;
  channel_path: string[];
}

export type IGetThreadDetailRes = {
  item: Pick<
    IThreadLisItem,
    | 'favorite'
    | 'digest'
    | 'praised'
    | 'favtimes'
    | 'praise'
    | 'views'
    | 'lastpost'
    | 'subject'
    | 'replies'
  > & {
    fid: number;
    tid: number;
    typeid: number;
    posttableid: number;
    type_name: string;
    // 正文（序列化）
    message: any[] | ReactHTML;
    devices: Array<NonNullable<IArticalAuther['device']>>;
    // 评论数
    comments: number;
    // 主题分享次数
    sharetimes: number;
    // 发表时间
    dateline: number;
    // 模块化帖子为 6
    special: number;
    // 置顶
    displayorder: DisplayOrderEnum;
    // 主题及板块信息
    forum_types: IForumType[];
    forum_channel_tags: IChannelTagType[];
    author: IAuhtor;
    authorid: number;
  };
};

export interface ISmileyRes {
  directory: string;
  name: string;
  type: string;
  smiles: IEmoji[];
  shown_on_reply: boolean;
}

export interface IGetCommentListReq {
  tid: string;
  offset: number;
  limit: number;
  sort?: string;
}

export enum COMMENT_STATUS {
  NORMAL = 0, // 正常
  IGNORED = -3, // 已忽略
  PENDING_REVIEW = -2, // 待审核
  RECYCLE_BIN = -5, // 回收站
  THREAD_IN_RECYCLE_BIN = -1, //主题帖在回收站中
}
export interface ICommentListRes {
  anonymous: string | number;
  comment: boolean;
  dateline: number;
  fid: number;
  is_stick: number;
  message: string;
  pid: string;
  tid: string;
  platform: number;
  position: number;
  likes: number;
  is_like: boolean;
  /** 表情包编码后的内容 */
  context: string;
  author: Omit<IArticalAuther, 'rank'> & {
    rank: NonNullable<IArticalAuther['rank']>;
  };
  quote?: {
    author: string;
    author_id: number;
    author_uuid: string;
    message: string;
    pid: string;
    position: number;
    status: COMMENT_STATUS;
  }
}

export interface IReplyCommentDetailRes {
  anonymous: number;
  author: string;
  authorid: number;
  dateline: number;
  fid: number;
  invisible: number;
  message: string;
  pid: number;
  platform: string;
  port: string;
  tid: number;
  useip: string;
}

/** API */
// 详情
export const getThreadDetailInfo = (
  isServerTrigger = false,
  tid: string | number,
  headers: any = {},
  device: string = 'desktop',
  adminToken?: string
) => {
  const url = `${isServerTrigger ? serverTriggerUrl : ''}${
    DETAIL_APIS.THREAD_DETAIL
  }${tid}`;
  const key = 'GetThreadDetailInfo';
  const params: any = { device };
  if (adminToken) {
    params.adminToken = adminToken;
  }
  return request({ isServerTrigger, url, key, headers, params })
    .then((data: IGetThreadDetailRes) => {
      if (data.item.special == 6) {
        // special 等于 6，模块化帖子，返回的是 JSON 字符串
        let message: any = data.item.message;
        const hasEditMsg = thread_reg.test(message);
        const messageMatch = message.match(thread_reg);
        if (hasEditMsg && messageMatch?.length) {
          message = message.replace(messageMatch[0], '').trim();
        }
        if (typeof message == 'string') {
          message = JSON.parse(
            message.replace(/\n/g, '\\n').replace(/\r/g, '\\r')
          );
          if (data.item.special === 6) {
            message = handleResourceUrl(message);
          }
          data.item.message = message;
        }
        return data;
      } else {
        // special 不等于 6，富文本帖子，返回的是 HTML
        return data;
      }
    })
    .catch((e) => {
      return Promise.reject(e);
    });
};

// Admin 处理帖子
export const operateThreadByAdmin = (params: any = {}) => {
  return request({
    url: DETAIL_APIS.OPERATE_THREAD_BY_ADMIN,
    method: MethodEnum.POST,
    params,
  })
    .then((data) => {})
    .catch((e) => {
      return e?.message;
    });
};

// 点赞
export const likeThread = (params: any = {}) => {
  return request({
    url: `${DETAIL_APIS.BASE_URL}${params.tid}${DETAIL_APIS.LIKE_THREAD}`,
    method: MethodEnum.POST,
    params,
  })
    .then((data) => data)
    .catch((e) => {});
};

// 取消点赞
export const unlikeThread = (params: any = {}) => {
  return request({
    url: `${DETAIL_APIS.BASE_URL}${params.tid}${DETAIL_APIS.LIKE_THREAD}`,
    method: MethodEnum.DELETE,
    params,
  })
    .then((data) => data)
    .catch((e) => {});
};

// 收藏
export const collectThread = (params: any = {}) => {
  return request({
    url: `${DETAIL_APIS.BASE_URL}${params.tid}${DETAIL_APIS.COLLECT_THREAD}`,
    method: MethodEnum.POST,
    params,
  })
    .then((data) => data)
    .catch((e) => {});
};

// 取消收藏
export const uncollectThread = (params: any = {}) => {
  return request({
    url: `${DETAIL_APIS.BASE_URL}${params.tid}${DETAIL_APIS.COLLECT_THREAD}`,
    method: MethodEnum.DELETE,
    params,
  })
    .then((data) => data)
    .catch((e) => {});
};

// 分享计数
export const shareThread = (params: any = {}) => {
  return request({
    url: `${DETAIL_APIS.BASE_URL}${params.tid}${DETAIL_APIS.SHARE_THREAD}`,
    method: MethodEnum.POST,
    params,
  })
    .then((data) => data)
    .catch((e) => {});
};

export interface IBannerAdRes {
  desc: string;
  img: string;
  url: string;
}

// 广告位
export const getDetailAd = (isServerTrigger = false) => {
  const url = `${isServerTrigger ? serverTriggerUrl : ''}${DETAIL_APIS.BANNER_AD}`;
  const key = 'GetDetailAdvertise';
  return request({ isServerTrigger, url, key }).then(data => {
    return data?.item;
  }).catch((e) => {
    Promise.reject(e);
  });
};

export interface IThreadRelated {
  tid: number;
  dateline: number;
  title: string;
  summary: string;
  author_id: number;
  author: Omit<IArticalAuther, 'rank'> & {
    rank: NonNullable<IArticalAuther['rank']>;
  };
  views: number;
}
// 相关推荐
export const getThreadRelated = (tid: string) => {
  const url = `forum/thread/${tid}/related`;
  return request({ url })
    .then((data) => data?.items)
    .catch((e) => Promise.reject(e));
};

// 创建回帖
export const replyThread = (params: {
  tid: string;
  parent_id?: string;
  payload: any;
}) => {
  return request({
    url: `${DETAIL_APIS.BASE_URL}${params.tid}${DETAIL_APIS.REPLY}`,
    method: MethodEnum.POST,
    params: {
      parent_id: params.parent_id,
      message: params.payload,
    },
  })
    .then((data) => data)
    .catch((e) => Promise.reject(e));
};

// 获取表情包列表
export const getEmojiList = (): Promise<ISmileyRes[]> => {
  return request({
    url: '/common/smiley',
    method: MethodEnum.GET,
  })
    .then((data) => data?.items)
    .catch((e) => Promise.reject(e));
};

// 获取评论列表
export const getCommentList = ({
  tid,
  offset,
  limit,
  sort = ThreadReplySortEnum.likesHot,
}: IGetCommentListReq):Promise<{ items: ICommentListRes[], total_items: number }> => {
  return request({
    url: `/forum/thread/${tid}/reply`,
    params: {
      offset,
      limit,
      sort,
    },
    method: MethodEnum.GET,
  })
    .then((data) => data)
    .catch((e) => Promise.reject(e));
};

// 处理帖子评论
export const replyThreadOperation = (params: any = {}) => {
  return request({
    url: DETAIL_APIS.REPLY_THREAD_OPERATION,
    method: MethodEnum.POST,
    params,
  })
    .then((data) => {})
    .catch((e) => {
      throw e;
    });
};

// 编辑-获取回帖详情
export const getReplyCommentDetail = ({
  pid,
}: {
  pid: string;
}): Promise<IReplyCommentDetailRes> => {
  return request({
    url: `/forum/thread/reply/${pid}`,
    method: MethodEnum.GET,
  })
    .then((data) => data.item)
    .catch((e) => Promise.reject(e));
};

// 修改回帖
export const changeComment = ({
  pid,
  payload,
}: {
  pid: string;
  payload: string;
}) => {
  return request({
    url: `/forum/thread/reply/${pid}`,
    method: MethodEnum.PUT,
    params: {
      message: payload,
    },
  })
    .then((data) => data.item)
    .catch((e) => Promise.reject(e));
};

// 评论-点赞/取消点赞
export const replyLike = ({pid, method}: {pid: string | number, method: MethodEnum}) => {
  return request({
    url: `/forum/thread/reply/${pid}/like`,
    method,
  }).then(data => data.item).catch((e) => Promise.reject(e));
};
